/**
 * PlotArtifact — renders a figure pinned to the canvas. Three payload
 * shapes supported:
 *   1. { plotly: {data, layout, config}, alt?, caption?, downloads? }
 *      — interactive Plotly chart from a backend plot tool
 *      (plot_scatter / plot_bar / plot_curves). Delegates to
 *      PlotlyChartRender so the same rendering applies on the admin
 *      sandbox + customer surface.
 *   2. { url, alt?, caption?, width?, height? } — server-rendered image
 *      (signed URL, used for legacy figure events).
 *   3. { echarts: {...spec} } — Phase 2 client-side ECharts payload.
 */
import { DataValueChip } from '../DataValueChip.jsx'
import { PlotlyChartRender } from '../PlotlyChartRender.jsx'
import { SalesRagonePlot } from './SalesRagonePlot.jsx'

/**
 * @param {object} props
 * @param {object} props.data — figure payload (see header)
 * @param {string} [props.caption]
 */
export function PlotArtifact({ data, caption }) {
  if (!data) return null

  // Path 0 — Sales-Selection ranked_cells_table with embedded plot_data.
  // Fired when the user is on the Plot tab while a rank_cells_for_application
  // artifact is pinned. See F1-PLAN §6.4 "Tab Plot zeigt Ragone-Scatter".
  if (data.artifact_type === 'ranked_cells_table' && data.plot_data) {
    return (
      <SalesRagonePlot
        plotData={data.plot_data}
        caption={caption || data.plot_data?.caption}
      />
    )
  }

  // Path 1 — interactive Plotly. The shape lands here whenever a plot_*
  // tool's output triggers chatStore.endToolCall → pinArtifact, since
  // that path passes the full output dict as `data`.
  if (data.plotly) {
    return (
      <PlotlyChartRender
        figure={data.plotly}
        alt={data.alt}
        caption={caption || data.caption}
        tool={data.tool}
        skippedCells={data.skipped_cells}
        isVerified={data.is_verified}
        chain={data.chain}
        handoff={data.explorer_handoff}
      />
    )
  }

  // Path 2 — single verified-data chip from get_cell_metric. The ToolCallCard
  // renders this inline as well; the canvas variant is for users who pin
  // a value to keep visible next to a chart.
  if (data.artifact_type === 'data_value' && data.cell && data.metric) {
    return (
      <div class="ion-data-value-pin">
        <DataValueChip
          cell={data.cell}
          metric={data.metric}
          formatted={data.formatted}
          basis={data.basis}
        />
      </div>
    )
  }

  if (data.url) {
    return (
      <figure class="ion-plot-artifact">
        <img
          src={data.url}
          alt={data.alt || caption || ''}
          width={data.width || undefined}
          height={data.height || undefined}
          class="ion-plot-artifact__img"
          loading="lazy"
        />
        {(caption || data.caption) && (
          <figcaption class="ion-plot-artifact__caption">
            {caption || data.caption}
          </figcaption>
        )}
      </figure>
    )
  }

  if (data.echarts) {
    return (
      <div class="ion-plot-artifact ion-plot-artifact--placeholder">
        <p>ECharts plot rendering arrives in Phase 2.</p>
        <pre class="ion-plot-artifact__raw">
          {JSON.stringify(data.echarts, null, 2).slice(0, 500)}
        </pre>
      </div>
    )
  }

  return (
    <div class="ion-plot-artifact ion-plot-artifact--fallback">
      <p>Unsupported plot shape — showing raw payload:</p>
      <pre class="ion-plot-artifact__raw">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  )
}
